import * as Haptics from "expo-haptics";
import { WeHonorLogomark } from "../src/components/logomark";
import { WeHonorWordmark } from "../src/components/wordmark";
import { useRouter } from "expo-router";
import {
  Check,
  ChevronRight,
  Flower2,
  Globe,
  Scan,
  Search,
  Sparkles,
  Store,
  Zap,
} from "lucide-react-native";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AccessibilityInfo,
  Animated,
  Easing,
  findNodeHandle,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { Badge, Button, Panel } from "../src/components/ui";
import { Reveal } from "../src/components/reveal";
import { AppShell } from "../src/components/layout";
import { useReducedMotion } from "../src/hooks/useReducedMotion";
import { useAppStore } from "../src/providers/AppProvider";
import { monarchFuneralHome, monarchImportCandidates } from "../src/data/monarch";
import { layout, motion, palette, radii, semantic, shadows, spacing } from "../src/theme/tokens";
import { FuneralHome } from "../src/types/domain";
import { trackFunnelStep } from "../src/lib/analytics";
import { supportsNativeDriver } from "../src/lib/animation";
import { getStepAnnouncement } from "../src/lib/accessibility";
import { normalizeDomain, isValidDomain, domainMatchesKnown } from "../src/lib/domain";

// ---------------------------------------------------------------------------
// Ambient backdrop (replicated from layout.tsx since not exported)
// ---------------------------------------------------------------------------

function AmbientBackdrop() {
  const { reduceMotion } = useReducedMotion();
  const driftA = useRef(new Animated.Value(0)).current;
  const driftB = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (reduceMotion) {
      driftA.setValue(0);
      driftB.setValue(0);
      return;
    }

    const createDrift = (value: Animated.Value) => {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(value, {
            toValue: 1,
            duration: 12000,
            useNativeDriver: supportsNativeDriver,
          }),
          Animated.timing(value, {
            toValue: 0,
            duration: 15000,
            useNativeDriver: supportsNativeDriver,
          }),
        ]),
      );
      loop.start();
      return loop;
    };

    const firstLoop = createDrift(driftA);
    let secondLoop: Animated.CompositeAnimation | undefined;
    const secondTimer = setTimeout(() => {
      secondLoop = createDrift(driftB);
    }, 2000);

    return () => {
      firstLoop.stop();
      secondLoop?.stop();
      clearTimeout(secondTimer);
    };
  }, [driftA, driftB, reduceMotion]);

  const transformA = [
    {
      translateX: driftA.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 20],
      }),
    },
    {
      translateY: driftA.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -15],
      }),
    },
  ];

  const transformB = [
    {
      translateX: driftB.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -25],
      }),
    },
    {
      translateY: driftB.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 10],
      }),
    },
  ];

  return (
    <View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, styles.ambientContainer]}
    >
      <Animated.View style={[styles.auraTop, reduceMotion ? null : { transform: transformA }]} />
      <Animated.View style={[styles.auraBottom, reduceMotion ? null : { transform: transformB }]} />
      <View style={styles.auraEdge} />
    </View>
  );
}

function GrainOverlay() {
  return (
    <View pointerEvents="none" style={[StyleSheet.absoluteFill, styles.grainContainer]}>
      <View style={styles.grain} />
    </View>
  );
}

// ---------------------------------------------------------------------------
// Animated step wrapper
// ---------------------------------------------------------------------------

function StepTransition({
  visible,
  children,
  announcement,
}: {
  visible: boolean;
  children: React.ReactNode;
  announcement: string;
}) {
  const { reduceMotion } = useReducedMotion();
  const opacity = useRef(new Animated.Value(reduceMotion ? 1 : 0)).current;
  const translateY = useRef(new Animated.Value(reduceMotion ? 0 : 30)).current;
  const headingRef = useRef<Text>(null);

  const focusHeading = useCallback(() => {
    if (Platform.OS === "web" && typeof document !== "undefined") {
      document.getElementById("onboarding-step-heading")?.focus();
      return;
    }

    const node = findNodeHandle(headingRef.current);
    if (node) AccessibilityInfo.setAccessibilityFocus(node);
  }, []);

  useEffect(() => {
    if (!visible) return;

    if (reduceMotion) {
      opacity.setValue(1);
      translateY.setValue(0);
      focusHeading();
    } else {
      opacity.setValue(0);
      translateY.setValue(30);
      const entrance = Animated.parallel([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 360,
          delay: 80,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: supportsNativeDriver,
        }),
        Animated.timing(translateY, {
          toValue: 0,
          duration: 360,
          delay: 80,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: supportsNativeDriver,
        }),
      ]);
      entrance.start(({ finished }) => {
        if (finished) focusHeading();
      });
      return () => entrance.stop();
    }
  }, [focusHeading, opacity, reduceMotion, translateY, visible]);

  if (!visible) return null;

  return (
    <Animated.View
      style={[{ opacity, flex: 1 }, reduceMotion ? null : { transform: [{ translateY }] }]}
    >
      <Text
        ref={headingRef}
        nativeID="onboarding-step-heading"
        accessible
        accessibilityRole="header"
        accessibilityLabel={announcement}
        {...(Platform.OS === "web" ? ({ tabIndex: -1 } as never) : {})}
        style={styles.visuallyHidden}
      >
        {announcement}
      </Text>
      {children}
    </Animated.View>
  );
}

const onboardingStepLabels = [
  "Welcome",
  "Connect your website",
  "Scanning",
  "Confirm your details",
  "Import complete",
] as const;

function getOnboardingStepAnnouncement(step: number) {
  return getStepAnnouncement(
    step + 1,
    onboardingStepLabels.length,
    onboardingStepLabels[step] ?? "Onboarding",
  );
}

// ---------------------------------------------------------------------------
// Step indicator dots
// ---------------------------------------------------------------------------

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <View
      accessible
      accessibilityRole="progressbar"
      accessibilityLabel={`Onboarding progress: step ${current + 1} of ${total}`}
      accessibilityValue={{ min: 1, max: total, now: current + 1 }}
      style={styles.stepIndicator}
    >
      {Array.from({ length: total }).map((_, i) => (
        <View
          key={i}
          style={[
            styles.stepDot,
            i === current ? styles.stepDotActive : null,
            i < current ? styles.stepDotComplete : null,
          ]}
        />
      ))}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Detection step line item
// ---------------------------------------------------------------------------

function DetectionLine({
  label,
  icon: Icon,
  active,
  complete,
  detail,
  delay,
}: {
  label: string;
  icon: typeof Globe;
  active: boolean;
  complete: boolean;
  detail?: string;
  delay: number;
}) {
  const { reduceMotion } = useReducedMotion();
  const opacity = useRef(new Animated.Value(reduceMotion ? 1 : 0)).current;
  const translateX = useRef(new Animated.Value(reduceMotion ? 0 : -12)).current;
  const checkScale = useRef(new Animated.Value(reduceMotion ? 1 : 0)).current;
  const spinValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!active && !complete) return;

    if (reduceMotion) {
      opacity.setValue(1);
      translateX.setValue(0);
      return;
    }

    opacity.setValue(0);
    translateX.setValue(-12);
    const entrance = Animated.parallel([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 400,
          delay,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: supportsNativeDriver,
        }),
        Animated.timing(translateX, {
          toValue: 0,
          delay,
          duration: 360,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: supportsNativeDriver,
        }),
      ]);
    entrance.start();
    return () => entrance.stop();
  }, [active, complete, delay, opacity, reduceMotion, translateX]);

  useEffect(() => {
    if (!active || complete || reduceMotion) {
      spinValue.stopAnimation();
      spinValue.setValue(0);
      return;
    }

    const spinLoop = Animated.loop(
        Animated.timing(spinValue, {
          toValue: 1,
          duration: 1200,
          easing: Easing.linear,
          useNativeDriver: supportsNativeDriver,
        }),
      );
    spinLoop.start();
    return () => spinLoop.stop();
  }, [active, complete, reduceMotion, spinValue]);

  useEffect(() => {
    if (complete) {
      spinValue.stopAnimation();
      if (reduceMotion) {
        checkScale.setValue(1);
        return;
      }
      checkScale.setValue(0);
      const check = Animated.timing(checkScale, {
        toValue: 1,
        duration: motion.stateDuration,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: supportsNativeDriver,
      });
      check.start();
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => undefined);
      return () => check.stop();
    }
  }, [complete, checkScale, reduceMotion, spinValue]);

  const spin = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "360deg"],
  });

  if (!active && !complete) return null;

  return (
    <Animated.View
      accessible
      accessibilityRole="text"
      accessibilityLiveRegion="polite"
      accessibilityLabel={`${label}${complete ? ". Complete" : ". In progress"}${detail && complete ? `. ${detail}` : ""}`}
      style={[
        styles.detectionLine,
        { opacity },
        reduceMotion ? null : { transform: [{ translateX }] },
      ]}
    >
      <View style={styles.detectionIconWrap}>
        {complete ? (
          <Animated.View style={reduceMotion ? null : { transform: [{ scale: checkScale }] }}>
            <View style={styles.checkCircle}>
              <Check color={palette.white} size={14} strokeWidth={2.5} />
            </View>
          </Animated.View>
        ) : (
          <Animated.View style={reduceMotion ? null : { transform: [{ rotate: spin }] }}>
            <Icon color={palette.gold} size={18} strokeWidth={1.8} />
          </Animated.View>
        )}
      </View>
      <View style={styles.detectionTextWrap}>
        <Text
          style={[
            styles.detectionLabel,
            complete ? styles.detectionLabelComplete : null,
          ]}
        >
          {label}
        </Text>
        {detail && complete ? (
          <Text style={styles.detectionDetail}>{detail}</Text>
        ) : null}
      </View>
    </Animated.View>
  );
}

// ---------------------------------------------------------------------------
// Main onboarding screen — Domain-first flow
// ---------------------------------------------------------------------------

export default function OnboardingScreen() {
  const router = useRouter();
  const { completeOnboarding, runImport } = useAppStore();
  const { reduceMotion } = useReducedMotion();

  // 0=Welcome, 1=Domain input, 2=Detection, 3=Confirmation form, 4=Import results
  const [step, setStep] = useState(0);

  // Domain input
  const [domain, setDomain] = useState("");
  const domainValid = isValidDomain(domain);

  // Pre-filled form (populated after detection)
  const [formName, setFormName] = useState("");
  const [formWebsite, setFormWebsite] = useState("");
  const [formOwner, setFormOwner] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formAddress, setFormAddress] = useState("");

  // Detection state
  const [detectStep, setDetectStep] = useState(0);

  // Completion state
  const [completing, setCompleting] = useState(false);
  const navigationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const confirmValid =
    formName.trim().length > 0 &&
    formEmail.trim().length > 0 &&
    formOwner.trim().length > 0;

  useEffect(() => () => {
    if (navigationTimer.current) clearTimeout(navigationTimer.current);
  }, []);

  // Run detection sequence
  useEffect(() => {
    if (step !== 2) return;

    const completeDetection = () => {
      setDetectStep(8);
      setFormName(monarchFuneralHome.name);
      setFormWebsite(monarchFuneralHome.websiteUrl);
      setFormPhone(monarchFuneralHome.phone);
      setFormAddress(monarchFuneralHome.address);
      trackFunnelStep("detected", normalizeDomain(domain));
      if (!reduceMotion) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => undefined);
      }
    };

    if (reduceMotion) {
      completeDetection();
      return;
    }

    const timers: ReturnType<typeof setTimeout>[] = [];

    timers.push(setTimeout(() => setDetectStep(1), 400));
    timers.push(setTimeout(() => setDetectStep(2), 1900));
    timers.push(setTimeout(() => setDetectStep(3), 3200));
    timers.push(setTimeout(() => setDetectStep(4), 4400));
    timers.push(setTimeout(() => setDetectStep(5), 5600));
    timers.push(setTimeout(() => setDetectStep(6), 7000));
    timers.push(setTimeout(() => setDetectStep(7), 8200));
    timers.push(
      setTimeout(() => {
        completeDetection();
      }, 9500),
    );

    return () => timers.forEach(clearTimeout);
  }, [domain, reduceMotion, step]);

  const handleGetStarted = useCallback(() => {
    if (!reduceMotion) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => undefined);
    setStep(1);
  }, [reduceMotion]);

  const handleConnectWebsite = useCallback(() => {
    if (!domainValid) return;
    if (!reduceMotion) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => undefined);
    trackFunnelStep("domain_captured", normalizeDomain(domain));
    setStep(2);
  }, [domainValid, domain, reduceMotion]);

  const handleProceedToConfirm = useCallback(() => {
    if (!reduceMotion) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => undefined);
    setStep(3);
  }, [reduceMotion]);

  const handleConfirmAndImport = useCallback(() => {
    if (!confirmValid) return;
    if (!reduceMotion) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => undefined);
    trackFunnelStep("details_confirmed", normalizeDomain(domain));
    setStep(4);
  }, [confirmValid, domain, reduceMotion]);

  const handleOpenDashboard = useCallback(async () => {
    setCompleting(true);
    if (!reduceMotion) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy).catch(() => undefined);

    const funeralHome: FuneralHome = {
      id: "fh-monarch",
      name: formName.trim() || monarchFuneralHome.name,
      websiteUrl: formWebsite.trim() || monarchFuneralHome.websiteUrl,
      ownerName: formOwner.trim() || monarchFuneralHome.ownerName,
      email: formEmail.trim() || monarchFuneralHome.email,
      phone: formPhone.trim() || monarchFuneralHome.phone,
      address: formAddress.trim() || monarchFuneralHome.address,
      providerLabel: "Tukios + Batesville",
      providerConfidence: "high",
      monitoring: {
        enabled: true,
        intervalMinutes: 30,
        lastScanAt: new Date().toISOString(),
        newCasesDetected: 0,
        notifyBeforeMinutes: 1440,
      },
      status: "active",
      createdAt: new Date().toISOString(),
    };

    completeOnboarding(funeralHome);
    await runImport();
    trackFunnelStep("import_complete", normalizeDomain(domain));

    navigationTimer.current = setTimeout(() => {
      navigationTimer.current = null;
      router.replace("/admin");
    }, reduceMotion ? 0 : 600);
  }, [
    completeOnboarding,
    runImport,
    router,
    domain,
    formName,
    formWebsite,
    formOwner,
    formEmail,
    formPhone,
    formAddress,
    reduceMotion,
  ]);

  const totalServices = monarchImportCandidates.reduce(
    (sum, c) => sum + c.services.length,
    0,
  );

  return (
    <AppShell role="public" accessibilityLabel="WeHonor onboarding">
      <View style={styles.root}>
        <AmbientBackdrop />
        <GrainOverlay />

        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === "ios" ? "padding" : undefined}
        >
          <ScrollView
            contentContainerStyle={styles.scroll}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.container}>
              {/* Brand header */}
              <Reveal delay={0}>
                <View style={styles.brandBar}>
                  <WeHonorLogomark size={44} />
                  <WeHonorWordmark color={palette.ink} size="lg" />
                </View>
              </Reveal>

              <StepIndicator current={step} total={5} />

              {/* --------------------------------------------------------- */}
              {/* STEP 0: Welcome                                            */}
              {/* --------------------------------------------------------- */}
              <StepTransition visible={step === 0} announcement={getOnboardingStepAnnouncement(0)}>
                <View style={styles.welcomeWrap}>
                  <Reveal delay={100}>
                    <Text style={styles.eyebrow}>FUNERAL HOME ONBOARDING</Text>
                  </Reveal>

                  <Reveal delay={200}>
                    <Text style={styles.heroTitle}>
                      Set up Central{"\n"}from your{"\n"}website
                    </Text>
                  </Reveal>

                  <Reveal delay={350}>
                    <Text style={styles.heroDescription}>
                      Enter your funeral home website. WeHonor will scan for
                      business details, active obituaries, and services, then
                      prepare what it finds for your review.
                    </Text>
                  </Reveal>

                  <Reveal delay={500}>
                    <View style={styles.welcomeFeatures}>
                      <FeatureRow
                        icon={Globe}
                        text="Auto-detect your website provider"
                      />
                      <FeatureRow
                        icon={Search}
                        text="Import active obituaries instantly"
                      />
                      <FeatureRow
                        icon={Sparkles}
                        text="Ready-to-go RSVP and check-in tools"
                      />
                    </View>
                  </Reveal>

                  <Reveal delay={650}>
                    <View style={styles.ctaWrap}>
                      <Button label="Get Started" onPress={handleGetStarted} />
                    </View>
                  </Reveal>
                </View>
              </StepTransition>

              {/* --------------------------------------------------------- */}
              {/* STEP 1: Domain-only input                                  */}
              {/* --------------------------------------------------------- */}
              <StepTransition visible={step === 1} announcement={getOnboardingStepAnnouncement(1)}>
                <View style={styles.formWrap}>
                  <Reveal delay={0}>
                    <Text style={styles.eyebrow}>CONNECT YOUR WEBSITE</Text>
                  </Reveal>
                  <Reveal delay={80}>
                    <Text style={styles.stepTitle}>
                      Enter your{"\n"}website address
                    </Text>
                  </Reveal>
                  <Reveal delay={160}>
                    <Text style={styles.stepDescription}>
                      We'll scan your site and prepare detected details for review.
                      Start with your website address; nothing is published automatically.
                    </Text>
                  </Reveal>

                  <Reveal delay={250}>
                    <Panel style={styles.domainPanel}>
                      <Text style={styles.domainPrefix}>https://www.</Text>
                      <TextInput
                        style={styles.domainInput}
                        value={domain}
                        onChangeText={setDomain}
                        placeholder="yourfuneralhome.com"
                        placeholderTextColor={palette.mist}
                        keyboardType="url"
                        autoCapitalize="none"
                        autoCorrect={false}
                        autoFocus
                        returnKeyType="go"
                        onSubmitEditing={handleConnectWebsite}
                      />
                    </Panel>
                  </Reveal>

                  <Reveal delay={400}>
                    <View style={styles.formActions}>
                      <Button
                        label="Connect My Website"
                        onPress={handleConnectWebsite}
                        disabled={!domainValid}
                      />
                      <View style={{ width: 12 }} />
                      <Button
                        label="Back"
                        variant="ghost"
                        compact
                        onPress={() => setStep(0)}
                      />
                    </View>
                  </Reveal>
                </View>
              </StepTransition>

              {/* --------------------------------------------------------- */}
              {/* STEP 2: Detection animation                                */}
              {/* --------------------------------------------------------- */}
              <StepTransition visible={step === 2} announcement={getOnboardingStepAnnouncement(2)}>
                <View style={styles.detectionWrap}>
                  <Reveal delay={0}>
                    <Text style={styles.eyebrow}>SCANNING</Text>
                  </Reveal>
                  <Reveal delay={80}>
                    <Text style={styles.stepTitle}>
                      Connecting to{"\n"}your website
                    </Text>
                  </Reveal>

                  <Reveal delay={200}>
                    <Panel spotlight style={styles.detectionPanel}>
                      <DetectionLine
                        icon={Globe}
                        label="Connecting to website..."
                        active={detectStep >= 1}
                        complete={detectStep >= 2}
                        detail={`Provider detected: Tukios + Batesville`}
                        delay={0}
                      />

                      {detectStep >= 2 ? (
                        <Animated.View style={styles.providerBadgeRow}>
                          <Badge label="Provider match: high confidence" tone="forest" />
                          <Badge label="Tukios + Batesville" tone="gold" />
                        </Animated.View>
                      ) : null}

                      <DetectionLine
                        icon={Store}
                        label="Extracting business information..."
                        active={detectStep >= 3}
                        complete={detectStep >= 4}
                        detail={`${monarchFuneralHome.name} • ${monarchFuneralHome.phone}`}
                        delay={0}
                      />

                      <DetectionLine
                        icon={Search}
                        label="Scanning current obituaries and services..."
                        active={detectStep >= 4}
                        complete={detectStep >= 5}
                        detail={`${monarchImportCandidates.length} obituaries, ${totalServices} services found`}
                        delay={0}
                      />

                      <DetectionLine
                        icon={Scan}
                        label="Extracting service details..."
                        active={detectStep >= 5}
                        complete={detectStep >= 6}
                        delay={0}
                      />

                      <DetectionLine
                        icon={Flower2}
                        label="Detecting flower and sympathy links..."
                        active={detectStep >= 6}
                        complete={detectStep >= 7}
                        detail="Flowers, cards, and tree links preserved"
                        delay={0}
                      />

                      <DetectionLine
                        icon={Zap}
                        label="Building your event kits..."
                        active={detectStep >= 7}
                        complete={detectStep >= 8}
                        delay={0}
                      />

                      {detectStep >= 8 ? (
                        <View style={styles.successReveal}>
                          <Reveal delay={0}>
                            <View style={styles.successBox}>
                              <View style={styles.successIconWrap}>
                                <Sparkles
                                  color={palette.gold}
                                  size={24}
                                  strokeWidth={1.6}
                                />
                              </View>
                              <Text style={styles.successTitle}>
                                Import Ready
                              </Text>
                              <Text style={styles.successBody}>
                                We found{" "}
                                <Text style={styles.successHighlight}>
                                  {monarchImportCandidates.length} active
                                  obituaries
                                </Text>{" "}
                                and{" "}
                                <Text style={styles.successHighlight}>
                                  {totalServices} services
                                </Text>{" "}
                                ready to import
                              </Text>
                              <View style={{ marginTop: 16 }}>
                                <Button
                                  label="Review & Confirm"
                                  onPress={handleProceedToConfirm}
                                />
                              </View>
                            </View>
                          </Reveal>
                        </View>
                      ) : null}
                    </Panel>
                  </Reveal>

                  {/* Pulsing progress bar */}
                  {detectStep < 8 ? (
                    <Reveal delay={300}>
                      <ProgressPulse progress={detectStep / 8} />
                    </Reveal>
                  ) : null}
                </View>
              </StepTransition>

              {/* --------------------------------------------------------- */}
              {/* STEP 3: Confirmation form (pre-filled)                     */}
              {/* --------------------------------------------------------- */}
              <StepTransition visible={step === 3} announcement={getOnboardingStepAnnouncement(3)}>
                <View style={styles.formWrap}>
                  <Reveal delay={0}>
                    <Text style={styles.eyebrow}>CONFIRM YOUR DETAILS</Text>
                  </Reveal>
                  <Reveal delay={80}>
                    <Text style={styles.stepTitle}>
                      Almost there
                    </Text>
                  </Reveal>
                  <Reveal delay={160}>
                    <Text style={styles.stepDescription}>
                      We auto-detected most of your information. Just confirm the
                      details below and add your name and email.
                    </Text>
                  </Reveal>

                  <Reveal delay={250}>
                    <Panel style={styles.formPanel}>
                      <View style={styles.formGroup}>
                        <View style={styles.formLabelRow}>
                          <Text style={styles.formLabel}>Funeral Home Name</Text>
                          {formName === monarchFuneralHome.name ? (
                            <Badge label="Auto-detected" tone="forest" />
                          ) : null}
                        </View>
                        <TextInput
                          style={[styles.input, formName === monarchFuneralHome.name && styles.inputAutoFilled]}
                          value={formName}
                          onChangeText={setFormName}
                          placeholder="Your Funeral Home Name"
                          placeholderTextColor={palette.mist}
                          autoCapitalize="words"
                          returnKeyType="next"
                        />
                      </View>

                      <View style={styles.formGroup}>
                        <View style={styles.formLabelRow}>
                          <Text style={styles.formLabel}>Website URL</Text>
                          <Badge label="Auto-detected" tone="forest" />
                        </View>
                        <TextInput
                          style={[styles.input, styles.inputAutoFilled]}
                          value={formWebsite}
                          onChangeText={setFormWebsite}
                          placeholder="https://www.yourfuneralhome.com"
                          placeholderTextColor={palette.mist}
                          keyboardType="url"
                          autoCapitalize="none"
                          autoCorrect={false}
                          returnKeyType="next"
                        />
                      </View>

                      <View style={styles.divider} />

                      <View style={styles.formGroup}>
                        <Text style={styles.formLabel}>Your Name</Text>
                        <TextInput
                          style={styles.input}
                          value={formOwner}
                          onChangeText={setFormOwner}
                          placeholder="Your name"
                          placeholderTextColor={palette.mist}
                          autoCapitalize="words"
                          autoFocus
                          returnKeyType="next"
                        />
                      </View>

                      <View style={styles.formGroup}>
                        <Text style={styles.formLabel}>Email</Text>
                        <TextInput
                          style={styles.input}
                          value={formEmail}
                          onChangeText={setFormEmail}
                          placeholder="admin@funeralhome.com"
                          placeholderTextColor={palette.mist}
                          keyboardType="email-address"
                          autoCapitalize="none"
                          autoCorrect={false}
                          returnKeyType="next"
                        />
                      </View>

                      <View style={styles.formRow}>
                        <View style={[styles.formGroup, { flex: 1 }]}>
                          <View style={styles.formLabelRow}>
                            <Text style={styles.formLabel}>Phone</Text>
                            {formPhone === monarchFuneralHome.phone ? (
                              <Badge label="Auto-detected" tone="forest" />
                            ) : null}
                          </View>
                          <TextInput
                            style={[styles.input, formPhone === monarchFuneralHome.phone && styles.inputAutoFilled]}
                            value={formPhone}
                            onChangeText={setFormPhone}
                            placeholder="(555) 123-4567"
                            placeholderTextColor={palette.mist}
                            keyboardType="phone-pad"
                            returnKeyType="next"
                          />
                        </View>
                      </View>

                      <View style={styles.formGroup}>
                        <View style={styles.formLabelRow}>
                          <Text style={styles.formLabel}>Address</Text>
                          {formAddress === monarchFuneralHome.address ? (
                            <Badge label="Auto-detected" tone="forest" />
                          ) : null}
                        </View>
                        <TextInput
                          style={[styles.input, formAddress === monarchFuneralHome.address && styles.inputAutoFilled]}
                          value={formAddress}
                          onChangeText={setFormAddress}
                          placeholder="123 Main Street, City, ST 00000"
                          placeholderTextColor={palette.mist}
                          autoCapitalize="words"
                          returnKeyType="done"
                        />
                      </View>
                    </Panel>
                  </Reveal>

                  <Reveal delay={400}>
                    <View style={styles.formActions}>
                      <Button
                        label="Confirm & Import"
                        onPress={handleConfirmAndImport}
                        disabled={!confirmValid}
                      />
                      <View style={{ width: 12 }} />
                      <Button
                        label="Back"
                        variant="ghost"
                        compact
                        onPress={() => setStep(2)}
                      />
                    </View>
                  </Reveal>
                </View>
              </StepTransition>

              {/* --------------------------------------------------------- */}
              {/* STEP 4: Import results                                     */}
              {/* --------------------------------------------------------- */}
              <StepTransition visible={step === 4} announcement={getOnboardingStepAnnouncement(4)}>
                <View style={styles.previewWrap}>
                  <Reveal delay={0}>
                    <Text style={styles.eyebrow}>IMPORT COMPLETE</Text>
                  </Reveal>
                  <Reveal delay={80}>
                    <Text style={styles.stepTitle}>
                      Your obituaries{"\n"}are ready
                    </Text>
                  </Reveal>
                  <Reveal delay={160}>
                    <Text style={styles.stepDescription}>
                      Each obituary is now a memorial with RSVP pages,
                      check-in tools, and guest management built in.
                    </Text>
                  </Reveal>

                  {monarchImportCandidates.map((candidate, i) => (
                    <Reveal key={candidate.decedentName} delay={250 + i * 120}>
                      <Panel style={styles.candidateCard}>
                        <View style={styles.candidateHeader}>
                          <View style={{ flex: 1 }}>
                            <Text style={styles.candidateName}>
                              {candidate.decedentName}
                            </Text>
                            {candidate.dob && candidate.dod ? (
                              <Text style={styles.candidateDates}>
                                {formatDate(candidate.dob)} &ndash;{" "}
                                {formatDate(candidate.dod)}
                              </Text>
                            ) : null}
                          </View>
                          <Badge label="High" tone="forest" />
                        </View>

                        <View style={styles.serviceList}>
                          {candidate.services.map((service, si) => (
                            <View key={si} style={styles.serviceRow}>
                              <View style={styles.serviceDot} />
                              <View style={{ flex: 1 }}>
                                <Text style={styles.serviceTitle}>
                                  {service.title}
                                </Text>
                                <Text style={styles.serviceVenue}>
                                  {service.venue}
                                </Text>
                                <Text style={styles.serviceMeta}>
                                  {formatDate(service.date)} at {formatTime(service.time)}
                                </Text>
                              </View>
                            </View>
                          ))}
                        </View>
                      </Panel>
                    </Reveal>
                  ))}

                  {/* Summary stat bar */}
                  <Reveal delay={250 + monarchImportCandidates.length * 120}>
                    <Panel spotlight style={styles.summaryBar}>
                      <View style={styles.summaryRow}>
                        <View style={styles.summaryItem}>
                          <Text style={styles.summaryValue}>
                            {monarchImportCandidates.length}
                          </Text>
                          <Text style={styles.summaryLabel}>Obituaries</Text>
                        </View>
                        <View style={styles.summaryDivider} />
                        <View style={styles.summaryItem}>
                          <Text style={styles.summaryValue}>
                            {totalServices}
                          </Text>
                          <Text style={styles.summaryLabel}>Services</Text>
                        </View>
                        <View style={styles.summaryDivider} />
                        <View style={styles.summaryItem}>
                          <Text style={[styles.summaryValue, { color: palette.success }]}>
                            Review
                          </Text>
                          <Text style={styles.summaryLabel}>Import status</Text>
                        </View>
                      </View>
                    </Panel>
                  </Reveal>

                  <Reveal
                    delay={400 + monarchImportCandidates.length * 120}
                  >
                    <View style={styles.formActions}>
                      <Button
                        label="Open Your Dashboard"
                        onPress={handleOpenDashboard}
                        loading={completing}
                        disabled={completing}
                      />
                    </View>
                  </Reveal>
                </View>
              </StepTransition>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Feature row for welcome screen
// ---------------------------------------------------------------------------

function FeatureRow({
  icon: Icon,
  text,
}: {
  icon: typeof Globe;
  text: string;
}) {
  return (
    <View style={styles.featureRow}>
      <View style={styles.featureIcon}>
        <Icon color={palette.forest} size={18} strokeWidth={1.8} />
      </View>
      <Text style={styles.featureText}>{text}</Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Pulsing progress bar
// ---------------------------------------------------------------------------

function ProgressPulse({ progress }: { progress: number }) {
  const { reduceMotion } = useReducedMotion();
  const pulseAnim = useRef(new Animated.Value(reduceMotion ? 1 : 0.4)).current;
  const widthAnim = useRef(new Animated.Value(reduceMotion ? progress : 0)).current;

  useEffect(() => {
    if (reduceMotion) {
      pulseAnim.setValue(1);
      return;
    }

    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 800,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
        Animated.timing(pulseAnim, {
          toValue: 0.4,
          duration: 800,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
      ]),
    );
    pulse.start();
    return () => pulse.stop();
  }, [pulseAnim, reduceMotion]);

  useEffect(() => {
    if (reduceMotion) {
      widthAnim.setValue(progress);
      return;
    }

    const width = Animated.timing(widthAnim, {
      toValue: progress,
      duration: 600,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    });
    width.start();
    return () => width.stop();
  }, [progress, reduceMotion, widthAnim]);

  const barWidth = widthAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["5%", "100%"],
  });

  return (
    <View style={styles.progressTrack}>
      <Animated.View
        style={[
          styles.progressFill,
          {
            width: barWidth,
            opacity: reduceMotion ? 1 : pulseAnim,
          },
        ]}
      />
    </View>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(timeStr: string): string {
  const parts = timeStr.split(":").map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${m.toString().padStart(2, "0")} ${suffix}`;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  visuallyHidden: {
    position: "absolute",
    left: -10000,
    top: 0,
    width: 1,
    height: 1,
    overflow: "hidden",
  },
  safeArea: {
    flex: 1,
    backgroundColor: palette.canvas,
  },
  root: {
    flex: 1,
    backgroundColor: palette.canvas,
  },
  scroll: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.xxl + 20,
  },
  container: {
    width: "100%",
    maxWidth: layout.readingWidth,
    alignSelf: "center",
    gap: spacing.lg,
  },

  // Brand
  brandBar: {
    gap: 2,
    paddingTop: 6,
    alignItems: "center",
  },
  brandWordmark: {
    color: palette.forest,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 22,
    lineHeight: 26,
  },

  // Step indicator
  stepIndicator: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 4,
  },
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: palette.mist,
  },
  stepDotActive: {
    width: 24,
    backgroundColor: palette.forest,
    borderRadius: 4,
  },
  stepDotComplete: {
    backgroundColor: palette.goldSoft,
  },

  // Eyebrow + titles
  eyebrow: {
    color: palette.cedar,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  heroTitle: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 52,
    lineHeight: 54,
    marginTop: 8,
  },
  heroDescription: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 16,
    lineHeight: 27,
    marginTop: 8,
    maxWidth: 560,
  },
  stepTitle: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 42,
    lineHeight: 44,
    marginTop: 6,
  },
  stepDescription: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 15,
    lineHeight: 25,
    maxWidth: 540,
  },

  // Welcome
  welcomeWrap: {
    gap: 4,
  },
  welcomeFeatures: {
    gap: 14,
    marginTop: 20,
  },
  featureRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
  },
  featureIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: semantic.status.success.background,
    alignItems: "center",
    justifyContent: "center",
  },
  featureText: {
    color: palette.ink,
    fontFamily: "Manrope_500Medium",
    fontSize: 15,
    lineHeight: 22,
    flex: 1,
  },
  ctaWrap: {
    marginTop: 28,
    alignItems: "flex-start",
  },

  // Domain input
  domainPanel: {
    marginTop: 12,
    gap: 8,
    paddingVertical: 20,
    paddingHorizontal: 20,
  },
  domainPrefix: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    marginBottom: -2,
  },
  domainInput: {
    borderWidth: 2,
    borderColor: palette.line,
    borderRadius: radii.lg,
    padding: 16,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 20,
    color: palette.ink,
    backgroundColor: semantic.surface.raised,
    minHeight: 56,
  },

  // Form
  formWrap: {
    gap: 8,
  },
  formPanel: {
    marginTop: 12,
    gap: 18,
  },
  formGroup: {
    gap: 6,
  },
  formLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  formRow: {
    flexDirection: "row",
    gap: 12,
  },
  formLabel: {
    color: palette.ink,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    letterSpacing: 0.2,
  },
  input: {
    borderWidth: 1,
    borderColor: palette.line,
    borderRadius: radii.md,
    padding: 14,
    fontFamily: "Manrope_400Regular",
    fontSize: 15,
    color: palette.ink,
    backgroundColor: semantic.surface.raised,
  },
  inputAutoFilled: {
    backgroundColor: semantic.surface.selected,
  },
  divider: {
    height: 1,
    backgroundColor: palette.line,
    opacity: 0.3,
    marginVertical: 4,
  },
  formActions: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 16,
  },

  // Detection
  detectionWrap: {
    gap: 8,
  },
  detectionPanel: {
    marginTop: 12,
    gap: 16,
    paddingVertical: 28,
  },
  detectionLine: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 14,
  },
  detectionIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: semantic.surface.canvasAlt,
    alignItems: "center",
    justifyContent: "center",
  },
  checkCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: semantic.status.success.foreground,
    alignItems: "center",
    justifyContent: "center",
  },
  detectionTextWrap: {
    flex: 1,
    gap: 3,
    paddingTop: 5,
  },
  detectionLabel: {
    color: palette.ink,
    fontFamily: "Manrope_500Medium",
    fontSize: 14,
    lineHeight: 21,
  },
  detectionLabelComplete: {
    color: palette.inkSoft,
  },
  detectionDetail: {
    color: palette.forest,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 14,
  },
  providerBadgeRow: {
    flexDirection: "row",
    gap: 8,
    paddingLeft: 46,
    marginTop: -4,
    marginBottom: 4,
  },

  // Success reveal
  successReveal: {
    marginTop: 8,
  },
  successBox: {
    backgroundColor: semantic.status.success.background,
    borderRadius: radii.md,
    padding: 24,
    alignItems: "center",
    borderWidth: 1,
    borderColor: semantic.border.success,
  },
  successIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: semantic.overlay.goldSubtle,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  },
  successTitle: {
    color: palette.forest,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 28,
    lineHeight: 30,
    marginBottom: 6,
  },
  successBody: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 15,
    lineHeight: 24,
    textAlign: "center",
  },
  successHighlight: {
    color: palette.ink,
    fontFamily: "Manrope_600SemiBold",
  },

  // Progress bar
  progressTrack: {
    height: 4,
    borderRadius: 2,
    backgroundColor: palette.mist,
    marginTop: 12,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 2,
    backgroundColor: palette.gold,
  },

  // Import preview
  previewWrap: {
    gap: 8,
  },
  candidateCard: {
    gap: 14,
  },
  candidateHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  candidateName: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 22,
    lineHeight: 26,
  },
  candidateDates: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 20,
    marginTop: 2,
  },
  serviceList: {
    gap: 10,
    paddingTop: 4,
  },
  serviceRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },
  serviceDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: palette.goldSoft,
    marginTop: 7,
  },
  serviceTitle: {
    color: palette.ink,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 14,
    lineHeight: 20,
  },
  serviceVenue: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 19,
  },
  serviceMeta: {
    color: palette.cedar,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 18,
  },

  // Summary bar
  summaryBar: {
    marginTop: 4,
  },
  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  summaryItem: {
    flex: 1,
    alignItems: "center",
    gap: 4,
  },
  summaryValue: {
    color: palette.forest,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 34,
    lineHeight: 36,
  },
  summaryLabel: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  summaryDivider: {
    width: 1,
    height: 36,
    backgroundColor: palette.line,
    opacity: 0.4,
  },

  // Ambient backdrop (replicated)
  grainContainer: {
    opacity: 0.03,
  },
  ambientContainer: {
    overflow: "hidden",
  },
  grain: {
    // StyleSheet.absoluteFillObject was removed from the RN 0.85 types.
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: palette.ink,
  },
  auraTop: {
    position: "absolute",
    top: -140,
    left: -80,
    width: 360,
    height: 360,
    borderRadius: 180,
    backgroundColor: semantic.overlay.gold,
  },
  auraBottom: {
    position: "absolute",
    bottom: 30,
    right: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    backgroundColor: semantic.overlay.forestSubtle,
  },
  auraEdge: {
    position: "absolute",
    top: 120,
    right: 80,
    width: 160,
    height: 160,
    borderRadius: 80,
    backgroundColor: semantic.overlay.cedarSubtle,
  },
});
